import { useEffect, useRef, useState } from 'react';

import {
    Mic,
    AudioLines,
    ChevronUp,
    Users,
    Monitor,
    Check
} from "lucide-react";



import { useNotes } from "../contexts/NotesContext"
import { useCommunication } from '@/lib/CommunicationWrapper';
import { CLOUDFLARE_URL } from '@/lib/config';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { AnimatePresence, motion } from 'framer-motion';

export const TranscribeButton = () => {
    const streamRef = useRef<MediaStream | null>(null);
    const [elapsedTime, setElapsedTime] = useState(0);
    const [useScreenAudio, setUseScreenAudio] = useState(false);
    const [screenAudioSupported, setScreenAudioSupported] = useState(true);
    const [isInitializing, setIsInitializing] = useState(false);
    const { token, updateTranscript, noteId, isRecording, setIsRecording, audioContextRef, analyserRef, animationFrameRef, waveformHistoryRef, maxHistoryLength, portal, setTranscriptTabOpen, transcriptTabOpen, setLiveTranscript, showRecordingDialog, setShowRecordingDialog } = useNotes();
    const beginRecording = useRef<number | null>(null);
    const recordingIntervalRef = useRef<NodeJS.Timeout | null>(null);
    const activeRecorder = useRef<MediaRecorder | null>(null);
    const standbyRecorder = useRef<MediaRecorder | null>(null);
    const processingCount = useRef<number>(0);
    const timerIntervalRef = useRef<NodeJS.Timeout | null>(null);
    const shouldStopProcessing = useRef<boolean>(false);

    const { loadSharedComponent, authenticatedClient, showError, trackEvent, updateOptions, checkGate, openGate, getOptions, authenticatedFetch } = useCommunication();


    const formatElapsedTime = (seconds: number) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    };

    const checkScreenAudioSupport = () => {
        const hasGetDisplayMedia = navigator.mediaDevices && navigator.mediaDevices.getDisplayMedia;
        const isChrome = /Chrome/.test(navigator.userAgent) && /Google Inc/.test(navigator.vendor);
        const isFirefox = navigator.userAgent.includes('Firefox');
        const isSafari = /Safari/.test(navigator.userAgent) && !/Chrome/.test(navigator.userAgent);

        // Basic support check
        if (!hasGetDisplayMedia) {
            setScreenAudioSupported(false);
            return;
        }

        // Additional browser-specific checks
        const supported = isChrome || isFirefox || isSafari;
        setScreenAudioSupported(supported);

        if (!supported) {
            console.log('🚫 Screen audio capture may not be fully supported in this browser');
        }
    };

    // Check browser support for screen audio capture
    useEffect(() => {
        checkScreenAudioSupport();
    }, []);

    // Auto-switch to microphone if screen audio is not supported
    useEffect(() => {
        if (!screenAudioSupported && useScreenAudio) {
            console.log('🔄 Screen audio not supported, switching to microphone');
            setUseScreenAudio(false);
        }
    }, [screenAudioSupported, useScreenAudio]);

    const getStream = async (useScreenAudio = false) => {
        console.log("Starting getStream function");

        try {
            const stream = await getAudioStream(useScreenAudio);
            if (stream) {
                console.log("✅ Stream obtained:", stream);
                console.log("Stream tracks:", stream.getTracks());
                streamRef.current = stream;
                return stream;
            } else {
                throw new Error("Failed to get audio stream");
            }
        } catch (err) {
            console.error("❌ getStream error:", err);
            throw err;
        }
    };

    const processAudioChunk = async (audioBlob: Blob) => {
        // Check if we should stop processing (synchronous check)
        if (shouldStopProcessing.current) {
            console.log('⏹️ Ignoring audio chunk - recording stopped');
            return;
        }

        processingCount.current++;

        try {
            const formData = new FormData();
            formData.append("file", audioBlob, "recording.webm");
            formData.append("noteId", noteId.toString() || "")

            // authenticatedFetch will automatically detect FormData and set the correct Content-Type
            const response = await authenticatedFetch(`${CLOUDFLARE_URL}/transcribe`, {
                method: "POST",
                body: formData,
            });

            const { message, error } = await response.json();

            // Handle usage limit errors (429 status)
            if (error?.code === 429 || response.status === 429 || error === "Usage limit exceeded") {
                // Show gate modal for transcript feature
                loadSharedComponent("TrialClaimGate", { highlight: "transcript" });
                // Stop processing immediately (synchronous)
                shouldStopProcessing.current = true;

                setIsRecording(false);
                setElapsedTime(0);
                if (recordingIntervalRef.current) {
                    clearInterval(recordingIntervalRef.current);
                    recordingIntervalRef.current = null;
                }
                if (timerIntervalRef.current) {
                    clearInterval(timerIntervalRef.current);
                    timerIntervalRef.current = null;
                }

                // Stop all media stream tracks to release microphone/screen audio
                if (streamRef.current) {
                    streamRef.current.getTracks().forEach(track => track.stop());
                    streamRef.current = null;
                }

                stopCurrentRecording();
                stopAudioAnalysis();
                return;
            }

            if (error) {
                return;
            }

            const messages = message || [];

            messages.forEach((msg: any) => {
                msg.timestamp = Date.now() - beginRecording.current! + (msg.start * 1000);
            });

            updateTranscript(messages);
        } catch (error) {
            console.error("Transcription failed:", error);
        } finally {
            processingCount.current--;
        }
    };

    const startNewRecording = () => {
        if (!streamRef.current || !standbyRecorder.current) return;

        if (activeRecorder.current?.state === 'recording') {
            activeRecorder.current.stop();
        }

        [activeRecorder.current, standbyRecorder.current] = [standbyRecorder.current, activeRecorder.current];
        activeRecorder.current.start();
    };

    const stopCurrentRecording = () => {
        [activeRecorder.current, standbyRecorder.current].forEach(recorder => {
            if (recorder) {
                // Remove event handler to prevent further processing
                recorder.ondataavailable = null;
                if (recorder.state === 'recording' || recorder.state === 'paused') {
                    recorder.stop();
                }
            }
        });
        // Clear the recorder refs
        activeRecorder.current = null;
        standbyRecorder.current = null;
    };

    useEffect(() => {
        if (!isRecording || !streamRef.current) return;

        // Check stream health
        const checkStream = () => {
            const tracks = streamRef.current?.getAudioTracks();
            if (!tracks?.length || tracks.some(t => t.readyState === 'ended')) {
                console.error('Stream lost, stopping recording');
                handleStopRecording(null);
                return false;
            }
            return true;
        };

        if (!checkStream()) return;

        activeRecorder.current?.start();
        recordingIntervalRef.current = setInterval(() => {
            if (checkStream()) startNewRecording();
        }, 5000);

        timerIntervalRef.current = setInterval(() => {
            setElapsedTime(prev => prev + 1);
        }, 1000);

        return () => {
            [recordingIntervalRef.current, timerIntervalRef.current].forEach(ref => {
                if (ref) clearInterval(ref);
            });
            recordingIntervalRef.current = timerIntervalRef.current = null;
            stopCurrentRecording();
        };
    }, [isRecording]);

    // Set up audio analysis when recording starts
    useEffect(() => {
        if (isRecording && streamRef.current && !analyserRef.current) {
            // Small delay to ensure everything is ready
            setTimeout(() => {
                if (streamRef.current) {
                    console.log('🎵 Setting up audio analysis...');
                    setupAudioAnalysis(streamRef.current);
                }
            }, 100);
        }
    }, [isRecording]);

    const setupAudioAnalysis = async (stream: MediaStream) => {
        try {
            console.log('🎵 Setting up audio analysis...');

            // Create audio context and analyser
            audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
            analyserRef.current = audioContextRef.current.createAnalyser();

            // Resume audio context if suspended (required by some browsers)
            if (audioContextRef.current.state === 'suspended') {
                await audioContextRef.current.resume();
                console.log('🎵 Audio context resumed');
            }

            // Connect stream to analyser
            const source = audioContextRef.current.createMediaStreamSource(stream);
            source.connect(analyserRef.current);

            // Configure analyser for direct audio feedback (no smoothing)
            analyserRef.current.fftSize = 512;
            analyserRef.current.smoothingTimeConstant = 0;

            console.log('🎵 Audio analysis setup complete');
        } catch (error) {
            console.error('❌ Error setting up audio analysis:', error);
        }
    };

    const stopAudioAnalysis = () => {
        if (animationFrameRef.current) {
            cancelAnimationFrame(animationFrameRef.current);
            animationFrameRef.current = null;
        }
        if (audioContextRef.current) {
            audioContextRef.current.close();
            audioContextRef.current = null;
        }
        analyserRef.current = null;
        // Clear waveform history
        waveformHistoryRef.current = [];
    };


    const handleTranscribeClick = async () => {
        trackEvent("transcribe_button_clicked");

        // Transcription is now available to all plan types
        // const { restricted } = await checkGate("transcript");
        // if (restricted === true) return;

        const options = getOptions();
        setTranscriptTabOpen(true);
        if (options.dont_show_recording_explanation === true) {
            // Start recording immediately
            startRecording();
        } else {
            // Show explanation dialog
            setShowRecordingDialog(true);
        }
    };

    const startRecording = async () => {
        setShowRecordingDialog(false);
        if (isRecording) {
            // Stop processing immediately (synchronous)
            shouldStopProcessing.current = true;

            setIsRecording(false);
            setElapsedTime(0);
            if (recordingIntervalRef.current) {
                clearInterval(recordingIntervalRef.current);
                recordingIntervalRef.current = null;
            }
            if (timerIntervalRef.current) {
                clearInterval(timerIntervalRef.current);
                timerIntervalRef.current = null;
            }

            // Stop all media stream tracks to release microphone/screen audio
            if (streamRef.current) {
                streamRef.current.getTracks().forEach(track => track.stop());
                streamRef.current = null;
            }

            stopCurrentRecording();
            stopAudioAnalysis();
        } else {
            try {
                console.log("🎙️ Starting recording process...");
                setIsInitializing(true);

                // Get audio stream first
                await getStream(useScreenAudio);

                if (streamRef.current) {
                    console.log("✅ Stream ready, setting up recorders...");

                    const createRecorder = () => {
                        const recorder = new MediaRecorder(streamRef.current!, { mimeType: "audio/webm" });
                        recorder.ondataavailable = async (e) => {
                            if (e.data.size > 0) await processAudioChunk(e.data);
                        };
                        return recorder;
                    };

                    activeRecorder.current = createRecorder();
                    standbyRecorder.current = createRecorder();

                    // Now that everything is ready, start recording
                    beginRecording.current = Date.now();
                    setElapsedTime(0);

                    // Allow processing chunks (synchronous)
                    shouldStopProcessing.current = false;

                    setIsRecording(true);
                    setIsInitializing(false);

                    console.log("🎯 Recording state set to true");
                } else {
                    throw new Error("Failed to obtain audio stream");
                }
            } catch (error) {
                console.error("Failed to start recording:", error);
                // Make sure we don't leave recording state as true if setup failed
                shouldStopProcessing.current = true;
                setIsRecording(false);
                setIsInitializing(false);

                // Clean up any partially created stream
                if (streamRef.current) {
                    streamRef.current.getTracks().forEach(track => track.stop());
                    streamRef.current = null;
                }
            } finally {
                const options = getOptions();
                if (options.has_used_transcribe !== true) {
                    updateOptions({ has_used_transcribe: true });
                }
            }
        }
    };

    async function getAudioStream(useScreenAudio = false) {
        try {
            let stream: MediaStream;

            if (useScreenAudio) {

                // Check if getDisplayMedia is supported
                if (!navigator.mediaDevices.getDisplayMedia) {
                    throw new Error('Screen capture is not supported in this browser.');
                }

                // Fallback: Include minimal video stream (some browsers require this)
                const displayMediaOptions = {
                    audio: {
                        echoCancellation: false,
                        noiseSuppression: false,
                        sampleRate: 44100,
                        suppressLocalAudioPlayback: false
                    },
                    video: {
                        width: { ideal: 1 },
                        height: { ideal: 1 },
                        frameRate: { ideal: 1 }
                    },
                    systemAudio: "include"
                } as any;

                stream = await navigator.mediaDevices.getDisplayMedia(displayMediaOptions);

                // Remove video tracks if we got them (we only want audio)
                const videoTracks = stream.getVideoTracks();
                videoTracks.forEach(track => {
                    track.stop();
                    stream.removeTrack(track);
                });


                // Verify we actually got audio tracks
                const audioTracks = stream.getAudioTracks();
                if (audioTracks.length === 0) {
                    stream.getTracks().forEach(track => track.stop());
                    throw new Error('No audio tracks available from screen capture. Make sure to select "Share audio" in the browser dialog.');
                }

                console.log('✅ Screen audio stream obtained');
            } else {
                // Use default microphone
                console.log('🎤 Requesting microphone access...');
                stream = await navigator.mediaDevices.getUserMedia({
                    audio: {
                        echoCancellation: true,
                        noiseSuppression: true,
                        sampleRate: 44100
                    }
                });
                console.log('✅ Microphone stream obtained');
            }

            // Log stream details
            const audioTracks = stream.getAudioTracks();
            console.log('Audio tracks:', audioTracks.map(track => ({
                label: track.label,
                kind: track.kind,
                enabled: track.enabled
            })));

            return stream;

        } catch (err: any) {
            console.error('Error accessing audio:', err);

            // Check specific error types
            if (err.name === 'NotAllowedError') {
                const source = useScreenAudio ? 'screen audio' : 'microphone';
                alert(`${source.charAt(0).toUpperCase() + source.slice(1)} access was denied. Please allow access in your browser settings.`);
            } else if (err.name === 'NotFoundError') {
                alert('No audio source found.');
            } else if (err.name === 'NotSupportedError') {
                alert('Audio capture is not supported in this browser.');
            } else {
                alert(`Audio error: ${err.message}`);
            }

            return null;
        }
    }

    const handleStopRecording = (e: React.MouseEvent<HTMLButtonElement>) => {
        e?.stopPropagation();
        e?.preventDefault();

        // Stop processing immediately (synchronous)
        shouldStopProcessing.current = true;

        setIsRecording(false);
        setElapsedTime(0);
        if (recordingIntervalRef.current) {
            clearInterval(recordingIntervalRef.current);
            recordingIntervalRef.current = null;
        }
        if (timerIntervalRef.current) {
            clearInterval(timerIntervalRef.current);
            timerIntervalRef.current = null;
        }

        // Stop all media stream tracks to release microphone/screen audio
        if (streamRef.current) {
            streamRef.current.getTracks().forEach(track => track.stop());
            streamRef.current = null;
        }

        stopCurrentRecording();
        stopAudioAnalysis();
    };

    const onClick = (e: React.MouseEvent<HTMLButtonElement>) => {
        if (isRecording) {
            handleStopRecording(e);
        } else {
            handleTranscribeClick();
        }
    };

    const toggleRecordingMode = () => {
        if (!isRecording && !isInitializing) {
            setUseScreenAudio(!useScreenAudio);
        }
    };

    return (
        <>
            <motion.div
                className={`absolute right-[50%] translate-x-[50%] z-15 flex items-center gap-[10px]`}
                animate={{
                    bottom: transcriptTabOpen ? '320px' : '24px',
                    transform: transcriptTabOpen ? 'translateY(50%)' : 'translateY(0)'
                }}
                transition={{
                    duration: 0.3,
                    ease: "easeInOut"
                }}
            >

                <button
                    onClick={onClick}
                    disabled={isInitializing}
                    className={`cursor-pointer flex items-center gap-[10px] border border-theme rounded-xl p-3 bg-theme-1 ${isRecording ? 'text-red-500' : isInitializing ? 'text-yellow-500' : 'text-gray-500'
                        } ${isInitializing ? 'opacity-75 cursor-wait' : ''}`}
                >
                    {isRecording ? (
                        <motion.div
                            animate={{
                                scale: [1, 1.2, 1],
                                opacity: [1, 0.8, 1]
                            }}
                            transition={{
                                duration: 1.5,
                                repeat: Infinity,
                                ease: "easeInOut"
                            }}
                        >
                            <AudioLines className="w-[20px] h-[20px]" />
                        </motion.div>
                    ) : (
                        <Mic className="size-[18px] text-theme-0" />
                    )}
                    {/*
                    {isRecording && (
                        <p className="text-sm font-medium text-theme-0">{formatElapsedTime(elapsedTime)}</p>
                    )}
                    */}
                    <span className="text-sm font-medium text-theme-0">{isRecording ? 'Stop' : 'Transcribe'}</span>
                </button>

                <button className="cursor-pointer flex items-center gap-[10px] border border-theme rounded-xl p-3 bg-theme-1" onClick={() => setTranscriptTabOpen((prev: boolean) => !prev)}>
                    <AnimatePresence>
                        <motion.div initial={{ rotate: 0 }} animate={{ rotate: transcriptTabOpen ? 180 : 0 }} transition={{ duration: 0.3 }}>
                            <ChevronUp className="size-4" />
                        </motion.div>
                    </AnimatePresence>
                </button>
            </motion.div>

            <Dialog open={showRecordingDialog} onOpenChange={setShowRecordingDialog}>
                <DialogContent className="max-w-md">
                    <RecordingExplanationDialog
                        startRecording={startRecording}
                        currentMode={useScreenAudio ? 'virtual' : 'physical'}
                        onClose={() => setShowRecordingDialog(false)}
                    />
                </DialogContent>
            </Dialog>
        </>
    );
};

const RecordingExplanationDialog = ({
    startRecording,
    currentMode,
    onClose
}: {
    startRecording: () => void;
    currentMode: 'physical' | 'virtual';
    onClose: () => void;
}) => {
    const [dontShowAgain, setDontShowAgain] = useState(false);
    const [selectedMode, setSelectedMode] = useState<'physical' | 'virtual'>(currentMode);

    const { updateOptions } = useCommunication();

    const handleStartRecording = () => {
        if (dontShowAgain) {
            updateOptions({ dont_show_recording_explanation: true });
        }
        startRecording();
    };

    return (
        <>
            <DialogHeader>
                <DialogTitle className="text-center">
                    <div className="flex items-center justify-center mb-3">
                        <div className="bg-theme-link p-4 rounded-xl">
                            <Mic className="size-8 text-theme-bg-0" />
                        </div>
                    </div>
                    <h3 className="text-xl font-bold text-theme-0 mb-3">
                        Ready to Record?
                    </h3>
                </DialogTitle>
            </DialogHeader>

            {/* Mode Switcher */}
            <div className="grid grid-cols-2 gap-3">
                <button
                    onClick={() => setSelectedMode('physical')}
                    className={`cursor-pointer p-4 rounded-xl border-2 transition-all duration-200 ${selectedMode === 'physical'
                        ? 'border-theme bg-theme-0 shadow-md'
                        : 'hover:bg-theme-2 border-transparent'
                        }`}
                >
                    <div className="flex flex-col items-center gap-2">
                        <Users className={`size-6 ${selectedMode === 'physical' ? 'text-theme-link' : 'text-theme-1'}`} />
                        <span className={`text-sm font-medium ${selectedMode === 'physical' ? 'text-theme-link' : 'text-theme-1'}`}>
                            In-Person
                        </span>
                    </div>
                </button>

                <button
                    onClick={() => setSelectedMode('virtual')}
                    className={`cursor-pointer p-4 rounded-xl border-2 transition-all duration-200 ${selectedMode === 'virtual'
                        ? 'border-theme bg-theme-0 shadow-md'
                        : 'hover:bg-theme-2 border-transparent'
                        }`}
                >
                    <div className="flex flex-col items-center gap-2">
                        <Monitor className={`size-6 ${selectedMode === 'virtual' ? 'text-theme-link' : 'text-theme-1'}`} />
                        <span className={`text-sm font-medium ${selectedMode === 'virtual' ? 'text-theme-link' : 'text-theme-1'}`}>
                            Online
                        </span>
                    </div>
                </button>
            </div>

            <div className="my-2">
                <div className="p-4 space-y-2.5">
                    {selectedMode === 'physical' ? (
                        <>
                            <div className="flex items-center gap-2.5">
                                <div className="bg-theme-link rounded-full p-0.5 flex-shrink-0">
                                    <Check className="size-4 text-theme-bg-0" />
                                </div>
                                <p className="text-sm text-theme-0">
                                    Allow microphone access when prompted
                                </p>
                            </div>
                            <div className="flex items-center gap-2.5">
                                <div className="bg-theme-link rounded-full p-0.5 flex-shrink-0">
                                    <Check className="size-4 text-theme-bg-0" />
                                </div>
                                <p className="text-sm text-theme-0">
                                    Ensure you have received consent to record
                                </p>
                            </div>
                        </>
                    ) : (
                        <>
                            <div className="flex items-center gap-2.5">
                                <div className="bg-theme-link rounded-full p-0.5 flex-shrink-0">
                                    <Check className="size-4 text-theme-bg-0" />
                                </div>
                                <p className="text-sm text-theme-0">
                                    Select the tab with audio
                                </p>
                            </div>
                            <div className="flex items-center gap-2.5">
                                <div className="bg-theme-link rounded-full p-0.5 flex-shrink-0">
                                    <Check className="size-4 text-theme-bg-0" />
                                </div>
                                <p className="text-sm text-theme-0">
                                    Check "Share audio" in the dialog
                                </p>
                            </div>
                            <div className="flex items-center gap-2.5">
                                <div className="bg-theme-link rounded-full p-0.5 flex-shrink-0">
                                    <Check className="size-4 text-theme-bg-0" />
                                </div>
                                <p className="text-sm text-theme-0">
                                    Ensure you have received consent to record
                                </p>
                            </div>
                        </>
                    )}
                </div>
            </div>

            {/*}
            <div className="flex items-center gap-2 mb-4 bg-theme-1 p-2.5 rounded-lg border border-theme">
                <input
                    type="checkbox"
                    id="dont-show-again"
                    checked={dontShowAgain}
                    onChange={(e) => setDontShowAgain(e.target.checked)}
                    className="w-4 h-4 rounded border-theme cursor-pointer accent-purple-600"
                />
                <label htmlFor="dont-show-again" className="text-sm cursor-pointer text-theme-0 select-none">
                    Don't show this again
                </label>
            </div>
            */}

            <DialogFooter className="gap-2">
                <button
                    onClick={handleStartRecording}
                    className="flex items-center justify-center gap-2 px-5 py-3.5 rounded-xl bg-theme-1 border border-theme text-theme-0 cursor-pointer font-medium transition-all duration-200"
                >
                    <Mic className="size-4" />
                    <span>Start Recording</span>
                </button>
            </DialogFooter>
        </>
    );
};